import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { 
  Settings, 
  Monitor, 
  Database, 
  CheckCircle,
  ArrowRight,
  Mail,
  Award,
  Users,
  Search,
  Target,
  Shield,
  Building2
} from "lucide-react";

const TechnicalAssistance = () => {
  const navigate = useNavigate();

  const services = [
    {
      icon: Settings,
      title: "Program Planning & Execution",
      description: "Comprehensive technical support for planning, implementing, and managing national programs and development schemes.",
      features: ["Strategic Planning", "Implementation Support", "Program Management", "Monitoring & Evaluation"]
    },
    {
      icon: Monitor,
      title: "MIS Development",
      description: "Custom Management Information Systems (MIS) development for data collection, analysis, and reporting requirements.",
      features: ["System Design", "Database Development", "User Interface", "Data Analytics"]
    },
    {
      icon: Database,
      title: "Data Management",
      description: "Complete data management solutions including collection, processing, analysis, and visualization for informed decision-making.",
      features: ["Data Collection", "Data Processing", "Statistical Analysis", "Data Visualization"]
    },
    {
      icon: Target,
      title: "Monitoring & Evaluation",
      description: "Comprehensive M&E frameworks and systems to track progress, measure impact, and ensure program effectiveness.",
      features: ["M&E Framework Design", "Indicator Development", "Impact Assessment", "Performance Tracking"]
    },
    {
      icon: Building2,
      title: "Capacity Building",
      description: "Technical training and capacity building programs for organizations and individuals in program management and implementation.",
      features: ["Technical Training", "Skill Development", "Tool Development", "Knowledge Transfer"]
    },
    {
      icon: Shield,
      title: "Quality Assurance",
      description: "Quality assurance and control mechanisms to ensure program standards, compliance, and continuous improvement.",
      features: ["Quality Standards", "Compliance Monitoring", "Process Improvement", "Best Practices"]
    }
  ];

  const stats = [
    { number: "100+", label: "Programs Supported", icon: Settings },
    { number: "10+", label: "MIS Systems Developed", icon: Monitor },
    { number: "95%", label: "Success Rate", icon: Target },
    { number: "8+", label: "Years Experience", icon: Award }
  ];

  const process = [
    {
      step: "01",
      title: "Assessment & Analysis",
      description: "Comprehensive assessment of technical requirements, existing systems, and development of customized solutions.",
      icon: Search
    },
    {
      step: "02",
      title: "Design & Development",
      description: "Systematic design and development of technical solutions, tools, and systems tailored to specific needs.",
      icon: Settings
    },
    {
      step: "03",
      title: "Implementation & Training",
      description: "Hands-on implementation support with comprehensive training and capacity building for sustainable adoption.",
      icon: Users
    },
    {
      step: "04",
      title: "Monitoring & Support",
      description: "Ongoing monitoring, maintenance, and technical support to ensure optimal performance and continuous improvement.",
      icon: Shield
    }
  ];

  const benefits = [
    "Comprehensive technical expertise",
    "Customized solution development",
    "Proven track record in program management",
    "Multi-sector experience",
    "End-to-end support services",
    "Cost-effective and efficient solutions"
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-gray-800 to-blue-900 text-white pt-24 sm:pt-32 pb-16 sm:pb-20 overflow-hidden">
        {/* Geometric Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-24 h-24 bg-white transform rotate-45"></div>
          <div className="absolute top-40 right-20 w-16 h-16 bg-white transform rotate-45"></div>
          <div className="absolute bottom-32 left-1/4 w-20 h-20 bg-white transform rotate-45"></div>
          <div className="absolute bottom-20 right-1/3 w-12 h-12 bg-white transform rotate-45"></div>
        </div>
        
        <div className="container-custom relative z-10 px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              <div className="space-y-6">
              <div className="inline-flex items-center px-6 py-3 bg-white/20 backdrop-blur-sm text-white rounded-full text-sm font-semibold border border-white/30">
                  <Settings className="h-5 w-5 mr-3" />
                  Technical Excellence
                </div>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight">
                  Comprehensive <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-300 to-cyan-300">Technical Support</span>
                </h1>
                <p className="text-xl text-white/90 leading-relaxed">
                  Complete technical assistance for planning, execution, MIS development, and monitoring of national programs and development schemes.
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <motion.button
                 onClick={()=>navigate('/contactus')}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-gradient-to-r from-blue-400 to-cyan-500 text-white px-8 py-4 rounded-xl font-semibold hover:shadow-lg transition-all duration-300 flex items-center justify-center"
                >
                  Get Technical Support
                  <ArrowRight className="ml-2 h-5 w-5" />
                </motion.button>
                <motion.button
                 onClick={()=>navigate('/projects')}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="border-2 border-white text-white px-8 py-4 rounded-xl font-semibold hover:bg-white hover:text-slate-900 transition-all duration-300"
                >
                  View Our Solutions
                </motion.button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 border border-white/20">
                <div className="space-y-6">
                  <h3 className="text-2xl font-bold text-white mb-6">Our Technical Impact</h3>
                  <div className="grid grid-cols-2 gap-6">
                    {stats.map((stat, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
                        className="text-center"
                      >
                        <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-3">
                          <stat.icon className="h-8 w-8 text-white" />
                        </div>
                        <div className="text-3xl font-bold text-white">{stat.number}</div>
                        <div className="text-white/80 text-sm">{stat.label}</div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container-custom px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Our Proven <span className="text-transparent bg-clip-text bg-gradient-to-r from-slate-600 to-blue-600">Technical Process</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A systematic approach that ensures effective technical solutions and successful program implementation.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="bg-white rounded-2xl p-8 h-full border border-gray-200 hover:border-slate-300 transition-all duration-300 hover:-translate-y-2 hover:shadow-xl">
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <div className="w-16 h-16 bg-gradient-to-r from-slate-600 to-blue-600 rounded-2xl flex items-center justify-center">
                        <step.icon className="h-8 w-8 text-white" />
                      </div>
                      <div className="text-4xl font-bold text-gray-200">{step.step}</div>
                    </div>
                    
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3">
                        {step.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </div>
                
                {index < process.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-0.5 bg-gradient-to-r from-slate-300 to-blue-300 transform -translate-y-1/2"></div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="container-custom px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Comprehensive <span className="text-transparent bg-clip-text bg-gradient-to-r from-slate-600 to-blue-600">Technical Solutions</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From program planning to MIS development, we provide end-to-end technical assistance that ensures successful implementation and monitoring.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group"
              >
                <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 h-full border border-gray-200 hover:border-slate-300 transition-all duration-300 hover:-translate-y-2 hover:shadow-xl">
                  <div className="space-y-6">
                    <div className="w-16 h-16 bg-gradient-to-r from-slate-600 to-blue-600 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <service.icon className="h-8 w-8 text-white" />
                    </div>
                    
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-slate-600 transition-colors">
                        {service.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed mb-4">
                        {service.description}
                      </p>
                      <ul className="space-y-2">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center text-sm text-gray-600">
                            <CheckCircle className="h-4 w-4 text-slate-500 mr-2 flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container-custom px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                Why Choose Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-slate-600 to-blue-600">Technical Assistance?</span>
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Partner with us for comprehensive technical solutions that ensure successful program implementation and sustainable development outcomes.
              </p>
              
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-center space-x-3"
                  >
                    <CheckCircle className="h-6 w-6 text-slate-600 flex-shrink-0" />
                    <span className="text-gray-700">{benefit}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-slate-600 to-blue-600 rounded-3xl p-8 text-white"
            >
              <div className="space-y-6">
                <div className="text-center">
                  <Settings className="h-16 w-16 mx-auto mb-4 text-white/80" />
                  <h3 className="text-2xl font-bold mb-2">Technical Excellence</h3>
                  <p className="text-white/90">Your success, our expertise</p>
                </div>
                
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
                  <h4 className="text-lg font-semibold mb-4">Get Technical Support</h4>
                  <p className="text-white/90 mb-6">
                    Ready to enhance your program implementation? Let's discuss how our technical assistance can help you achieve your development goals.
                  </p>
                  <motion.button
                    onClick={() => navigate('/contactus')}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="w-full bg-white text-slate-600 px-6 py-3 rounded-xl font-semibold hover:bg-gray-100 transition-all duration-300 flex items-center justify-center"
                  >
                    Contact Us Now
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-slate-600 to-blue-600">
        <div className="container-custom px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center text-white"
          >
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">
              Ready to Enhance Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 to-orange-300">Program Implementation</span>?
            </h2>
            <p className="text-xl mb-8 max-w-3xl mx-auto text-white/90">
              Let's discuss your technical assistance needs and create comprehensive solutions that ensure successful program delivery and monitoring.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
      
              <motion.button
               onClick={()=>navigate('/contactus')}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="border-2 border-white text-white px-8 py-4 rounded-xl font-semibold hover:bg-white hover:text-slate-600 transition-all duration-300 flex items-center justify-center"
              >
                <Mail className="mr-2 h-5 w-5" />
                Discuss Your Needs
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default TechnicalAssistance;